import React, { useEffect, useMemo, useState } from 'react';
import { Save } from 'lucide-react';
import { toast } from 'sonner';

import { authApi } from '../../api/examApi';
import { parseApiError } from '../../lib/apiErrors';
import { StudentLayout } from '../../components/layout/StudentLayout';
import { useAuth } from '../../context/AuthContext';
import {
  CLASS_LEVELS, BOARDS, COMPETITIVE_EXAMS, STUDY_MODES, SUBJECT_COMBOS, useSubjectOptions, subjectIdsFor,
  SelectField, TextField, SectionHeading, SubjectMultiSelect,
} from '../../components/student/StudentProfileFields';
import { StudentPhotoUpload } from '../../components/student/StudentPhotoUpload';

const EMPTY_FORM = {
  name: '',
  phone_number: '',
  main_contact_number: '',
  class_level: '',
  board: '',
  board_other: '',
  competitive_exam: '',
  study_mode: 'INDEPENDENT',
  subjects: [],
  father_name: '',
  father_mobile: '',
  father_email: '',
  mother_name: '',
  mother_mobile: '',
  mother_email: '',
};

export default function StudentProfilePage() {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(EMPTY_FORM);
  const subjectOptions = useSubjectOptions();

  useEffect(() => {
    authApi.getStudentProfile()
      .then(({ data }) => {
        const subjects = Array.isArray(data.subjects) ? data.subjects : [];
        setFormData({
          name: data.name || '',
          phone_number: data.phone_number || '',
          main_contact_number: data.main_contact_number || '',
          class_level: data.class_level != null ? String(data.class_level) : '',
          board: data.board || '',
          board_other: data.board_other || '',
          competitive_exam: data.competitive_exam || '',
          study_mode: data.study_mode || 'INDEPENDENT',
          subjects,
          father_name: data.father_name || '',
          father_mobile: data.father_mobile || '',
          father_email: data.father_email || '',
          mother_name: data.mother_name || '',
          mother_mobile: data.mother_mobile || '',
          mother_email: data.mother_email || '',
        });
      })
      .catch((err) => toast.error(parseApiError(err, 'Failed to load profile').message))
      .finally(() => setLoading(false));
  }, []);

  const profileCompleteness = useMemo(() => {
    const hasSubjects = formData.subjects.length > 0;
    const checks = [
      formData.name,
      formData.phone_number,
      formData.class_level,
      formData.board,
      hasSubjects,
      formData.father_name || formData.mother_name,
    ];
    const done = checks.filter(Boolean).length;
    return Math.round((done / checks.length) * 100);
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const toggleSubject = (subject) => {
    setFormData((prev) => {
      const selected = prev.subjects.includes(subject)
        ? prev.subjects.filter((s) => s !== subject)
        : [...prev.subjects, subject];
      return { ...prev, subjects: selected };
    });
  };

  const applyCombo = (combo) => {
    setFormData((prev) => {
      const current = new Set(prev.subjects);
      const allSelected = combo.subjects.every((s) => current.has(s));
      if (allSelected) {
        combo.subjects.forEach((s) => current.delete(s));
      } else {
        combo.subjects.forEach((s) => current.add(s));
      }
      return { ...prev, subjects: [...current] };
    });
  };

  const isComboActive = (combo) => combo.subjects.every((s) => formData.subjects.includes(s));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    try {
      const subjectIds = subjectIdsFor(formData.subjects, subjectOptions.options);
      if (subjectIds === null) {
        toast.error('Subject list is not loaded yet or is out of date. Reload the page and try again.');
        setSaving(false);
        return;
      }
      const payload = {
        name: formData.name || undefined,
        phone_number: formData.phone_number || null,
        main_contact_number: formData.main_contact_number || null,
        class_level: formData.class_level ? parseInt(formData.class_level, 10) : null,
        board: formData.board || null,
        board_other: formData.board === 'OTHER' ? (formData.board_other || null) : null,
        competitive_exam: formData.competitive_exam || null,
        study_mode: formData.study_mode || null,
        subject_ids: subjectIds,
        father_name: formData.father_name || null,
        father_mobile: formData.father_mobile || null,
        father_email: formData.father_email || null,
        mother_name: formData.mother_name || null,
        mother_mobile: formData.mother_mobile || null,
        mother_email: formData.mother_email || null,
      };
      const { data } = await authApi.updateStudentProfile(payload);
      updateUser({ ...user, name: data.name, email: data.email });
      toast.success('Profile updated');
    } catch (err) {
      const { fieldErrors, message } = parseApiError(err, 'Failed to update profile');
      setErrors(fieldErrors);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <StudentLayout title="My Profile">
        <div className="space-y-4 animate-pulse">
          <div className="h-32 rounded-lg bg-slate-200 dark:bg-slate-700" />
          <div className="h-96 rounded-lg bg-slate-200 dark:bg-slate-700" />
        </div>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout title="My Profile">
      <div className="animate-fade-in space-y-6">
        {/* Header card */}
        <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <StudentPhotoUpload
                fetchPhoto={() => authApi.getStudentPhoto(user?.user_id)}
                uploadPhoto={(formData) => authApi.uploadMyPhoto(formData)}
              />
              <div>
                <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
                  {formData.name || user?.name}
                </h2>
                <p className="text-slate-500 dark:text-slate-400">{user?.email}</p>
              </div>
            </div>
            <div className="rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
              Profile {profileCompleteness}% complete
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Info */}
          <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <SectionHeading>Personal Information</SectionHeading>
            <div className="grid gap-6 md:grid-cols-2">
              <TextField label="Full Name" name="name" value={formData.name} error={errors.name} onChange={handleChange} />
              <TextField label="Student Contact" name="phone_number" value={formData.phone_number} error={errors.phone_number} onChange={handleChange} type="tel" />
              <TextField label="Main Contact Number" name="main_contact_number" value={formData.main_contact_number} error={errors.main_contact_number} onChange={handleChange} type="tel" placeholder="Primary number to reach the family" />
            </div>
          </div>

          {/* Academic Profile */}
          <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <SectionHeading>Academic Profile</SectionHeading>
            <div className="grid gap-6 md:grid-cols-2">
              <SelectField
                label="Class"
                name="class_level"
                value={formData.class_level}
                error={errors.class_level}
                onChange={handleChange}
                options={CLASS_LEVELS.map((n) => ({ value: String(n), label: `Class ${n}` }))}
              />
              <SelectField
                label="Board"
                name="board"
                value={formData.board}
                error={errors.board}
                onChange={handleChange}
                options={BOARDS}
              />
              {formData.board === 'OTHER' && (
                <div className="md:col-span-2">
                  <TextField
                    label="Specify Board"
                    name="board_other"
                    value={formData.board_other}
                    error={errors.board_other}
                    onChange={handleChange}
                    placeholder="Enter board name"
                  />
                </div>
              )}
              <SelectField
                label="Competitive Exam Target"
                name="competitive_exam"
                value={formData.competitive_exam}
                error={errors.competitive_exam}
                onChange={handleChange}
                options={COMPETITIVE_EXAMS}
              />
              <SelectField
                label="Study Mode"
                name="study_mode"
                value={formData.study_mode}
                error={errors.study_mode}
                onChange={handleChange}
                options={STUDY_MODES.map((m) => ({ value: m, label: m.charAt(0) + m.slice(1).toLowerCase() }))}
              />
            </div>

            {/* Subjects */}
            <div className="mt-6">
              <label className="mb-3 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Subjects
              </label>
              {/* Combo presets */}
              <div className="mb-3 flex flex-wrap gap-2">
                {SUBJECT_COMBOS.map((combo) => (
                  <button
                    key={combo.label}
                    type="button"
                    onClick={() => applyCombo(combo)}
                    className={[
                      'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                      isComboActive(combo)
                        ? 'border-primary bg-primary text-white dark:border-accent-cool dark:bg-accent-cool'
                        : 'border-slate-300 bg-white text-slate-600 hover:border-primary hover:text-primary dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:border-accent-cool dark:hover:text-accent-cool',
                    ].join(' ')}
                  >
                    {combo.label}
                  </button>
                ))}
              </div>
              {/* Compact multi-select checklist */}
              <SubjectMultiSelect
                options={subjectOptions.names}
                selected={formData.subjects}
                onToggle={toggleSubject}
                loading={!subjectOptions.loaded}
              />
            </div>
          </div>

          {/* Parent / Guardian Info */}
          <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <SectionHeading>Parent / Guardian Information</SectionHeading>
            <div className="grid gap-8 md:grid-cols-2">
              {/* Father */}
              <div className="space-y-4">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Father</p>
                <TextField label="Father's Name" name="father_name" value={formData.father_name} error={errors.father_name} onChange={handleChange} />
                <TextField label="Father's Mobile" name="father_mobile" value={formData.father_mobile} error={errors.father_mobile} onChange={handleChange} type="tel" />
                <TextField label="Father's Email" name="father_email" value={formData.father_email} error={errors.father_email} onChange={handleChange} type="email" />
              </div>
              {/* Mother */}
              <div className="space-y-4">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Mother</p>
                <TextField label="Mother's Name" name="mother_name" value={formData.mother_name} error={errors.mother_name} onChange={handleChange} />
                <TextField label="Mother's Mobile" name="mother_mobile" value={formData.mother_mobile} error={errors.mother_mobile} onChange={handleChange} type="tel" />
                <TextField label="Mother's Email" name="mother_email" value={formData.mother_email} error={errors.mother_email} onChange={handleChange} type="email" />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 font-medium text-white hover:bg-primary-light disabled:opacity-50"
            >
              <Save size={18} />
              {saving ? 'Saving…' : 'Save Profile'}
            </button>
          </div>
        </form>
      </div>
    </StudentLayout>
  );
}
